import React from "react";
import styled from "styled-components";
import IconSummary from "../IconSummary/index";
import { colorFunctionalGray60, colorFunctionalGray70, colorMiscGray40, IconAbsent, IconAddModifier, IconCostCode, IconShiftExtra, IconSignature, IconTimeCard, } from "@rhumbix/rmbx_design_system_design_tokens";
import EmployeeInfo from "../EmployeeInfo/index";
/**
 * A card displaying some summary TK information for an employee
 * @param {number} absenceCount Number of absences
 * @param {number} costCodeCount Number of cost codes
 * @param {boolean} hideShiftExtras Hide the shift extra icon
 * @param {Record<string, string>} employeeInfo Employee information
 * @param {Record<string, any>} modifiers Dictionary of modifier information for the employee
 * @param {number} shiftAndBreakCount Number of Shifts + Breaks
 * @param {number} shiftExtraCount Number of shift extras
 * @param {number} signatureCount Number of signatures required (0 or 1)
 * @param {string} tradeClassification String representing the employee's trade & classification
 * @param {number} totalMinutes Total number of minutes worked
 * @param {number} version Which version to use (v1 includes signature count, v2 does not)
 * @constructor
 */
const EmployeeTkSummaryCard = ({ absenceCount, costCodeCount, employeeInfo, hideShiftExtras, modifiers, shiftAndBreakCount, shiftExtraCount, signatureCount, tradeClassification, totalMinutes, version = 1, }) => {
    // Loop through any modifier data and generate the list of components to represent them
    const modifierBits = Object.entries(modifiers).map(([modifierName, modDict]) => {
        return (React.createElement(EmployeeModifier, { key: modifierName },
            React.createElement(ModifierInfo, null,
                React.createElement(IconAddModifier, { height: 16 }),
                React.createElement(EmployeeDetails, { style: { marginLeft: "10px" } }, modifierName)),
            React.createElement(TimeSummary, null,
                React.createElement(IconSummary, { iconPosition: "top", icon: () => React.createElement(IconCostCode, { height: 12, color: colorFunctionalGray70 }), summaryText: modDict.costCodeCount ? modDict.costCodeCount.toString() : "--" }),
                React.createElement(VerticalRule, { style: { marginLeft: "15px" } }),
                React.createElement(TimeTotal, null,
                    React.createElement(HourTotal, null,
                        Math.floor(modDict.totalMinutes / 60),
                        ":",
                        (modDict.totalMinutes % 60).toLocaleString("en-US", {
                            minimumIntegerDigits: 2,
                            useGrouping: false,
                        }))))));
    });
    return (React.createElement(EmployeeCardContainer, null,
        React.createElement(UnmodifiedSection, null,
            React.createElement(EmployeeInfo, { firstName: employeeInfo.firstName, lastName: employeeInfo.lastName, id: employeeInfo.id, tradeClassification: tradeClassification }),
            React.createElement(TimeSummary, null,
                React.createElement(IconSlot, null,
                    React.createElement(IconSummary, { iconPosition: "top", icon: () => React.createElement(IconAbsent, { height: 12, color: colorFunctionalGray70 }), summaryText: absenceCount ? absenceCount.toString() : "--" })),
                (version === 1 || !hideShiftExtras) && (React.createElement(IconSlot, null,
                    React.createElement(IconSummary, { iconPosition: "top", icon: () => React.createElement(IconShiftExtra, { height: 12, color: colorFunctionalGray70 }), summaryText: shiftExtraCount ? shiftExtraCount.toString() : "--" }))),
                version == 1 && (React.createElement(IconSlot, null,
                    React.createElement(IconSummary, { iconPosition: "top", icon: () => React.createElement(IconSignature, { height: 12, color: colorFunctionalGray70 }), summaryText: signatureCount ? signatureCount.toString() : "--" }))),
                version > 2 && (React.createElement(IconSlot, null,
                    React.createElement(IconSummary, { iconPosition: "top", icon: () => React.createElement(IconTimeCard, { height: 12, color: colorFunctionalGray70 }), summaryText: shiftAndBreakCount ? shiftAndBreakCount.toString() : "--" }))),
                React.createElement(IconSlot, null,
                    React.createElement(IconSummary, { iconPosition: "top", icon: () => React.createElement(IconCostCode, { height: 12, color: colorFunctionalGray70 }), summaryText: costCodeCount ? costCodeCount.toString() : "--" })),
                " ",
                React.createElement(VerticalRule, null),
                React.createElement(TimeTotal, null,
                    React.createElement(TotalText, null, "TOTAL"),
                    React.createElement(HourTotal, null,
                        Math.floor(totalMinutes / 60),
                        ":",
                        (totalMinutes % 60).toLocaleString("en-US", {
                            minimumIntegerDigits: 2,
                            useGrouping: false,
                        }))))),
        modifierBits));
};
const EmployeeCardContainer = styled.div `
    display: flex;
    flex-direction: column;
`;
const UnmodifiedSection = styled.div `
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
`;
const EmployeeDetails = styled.div `
    font-size: 12px;
    font-weight: 400;
    color: ${colorFunctionalGray60};
`;
const TimeSummary = styled.div `
    flex-direction: row;
    display: flex;
    margin-top: 10px;
`;
const IconSlot = styled.div `
    margin-left: 15px;
    margin-right: 15px;
`;
const VerticalRule = styled.div `
    border-left: 1px solid ${colorMiscGray40};
    height: 28px;
    margin-top: 10px;
`;
const TimeTotal = styled.div `
    flex-direction: column;
    margin-left: 10px;
    text-align: center;
    margin-top: 2px;
    width: 35px;
`;
const HourTotal = styled.div `
    font-size: 14px;
    font-weight: 500;
    margin-top: 7px;
`;
const TotalText = styled.div `
    font-size: 12px;
    font-weight: 500;
`;
const EmployeeModifier = styled.div `
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
`;
const ModifierInfo = styled.div `
    flex-direction: row;
    display: flex;
    margin-top: 10px;
`;
export default EmployeeTkSummaryCard;
